import { LoadingFallback } from "components/common";
import useMergeState from "hooks/useMergeState";
import { Profile } from "models";
import moment from "moment";
import { createContext, ReactNode, useContext, useEffect } from "react";
import {
  matchPath,
  RouteObject,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { routes } from "routes";
import { accountService } from "services";
import { notificationSocketService } from "services/notificationSocket.service";
import {
  getAccessToken,
  getAuthenticationResult,
  getRefreshToken,
  // getRemember,
  removeToken,
  setToken,
} from "services/token.service";
import { AppRouteType } from "types";
import { AuthenticationResult } from "types/auth.types";
import {
  ResponseForgot,
  ResponseReset,
  SuccessResponse,
} from "types/reponse.types";
interface AuthContextType {
  loading: boolean;
  accessToken: null | string | undefined;
  isAuth: boolean;
  profile: Profile | null;
  onSignIn: (credentials: {
    email: string;
    password: string;
    remember: boolean;
  }) => Promise<void>;
  onSignOut: () => void;
  onRefreshToken: () => void;
  getProfile: () => void;
  onForgot: (credentials: {
    email: string;
    frontendUrl: string;
  }) => Promise<ResponseForgot>;
  onVerify: (credentials: { token: string }) => Promise<ResponseReset>;
  onResetPass: (credentials: {
    email: string;
    password: string;
    token: string;
    frontendUrl: string;
  }) => Promise<ResponseReset>;
}

const AuthContext = createContext<AuthContextType>({
  loading: false,
  accessToken: null,
  isAuth: false,
  profile: null,
  onSignIn: async () => Promise.resolve(),
  onSignOut: () => {},
  onRefreshToken: () => {},
  getProfile: () => {},
  onForgot: async () =>
    Promise.resolve({
      statusCode: 500,
      message: "Default error",
      success: true,
    }),
  onVerify: async () => Promise.resolve({ email: "", success: true }),
  onResetPass: async () => Promise.resolve({ email: "", success: true }),
});

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const tokenStorage = getAuthenticationResult();
  const [userData, setUserData] = useMergeState<{
    isFirstLoading: boolean;
    loading: boolean;
    accessToken: string | null;
    isAuth: boolean;
    profile: Profile | null;
    remember?: boolean;
  }>({
    isFirstLoading: false,
    loading: false,
    accessToken: tokenStorage?.accessToken || null,
    // accessToken: "test",
    isAuth: false,
    // profile: {
    //   data: {
    //     id: "63f21513-219f-4eb3-a8cf-916669d1e07c",
    //     birthday: null,
    //     gender: "male",
    //     citizenId: null,
    //     citizenIdDate: null,
    //     citizenIdPlace: null,
    //     startWorkingDate: null,
    //     salary: null,
    //     note: "",
    //     baseUserId: "e890a194-84dd-485a-aa1d-7eba945b0e75",
    //     roleId: "ee471706-6ace-456e-870b-6c36d4614a77",
    //     createdAt: "2025-04-14T16:51:50.118Z",
    //     updatedAt: "2025-09-26T18:00:45.802Z",
    //     deletedAt: null,
    //     baseUser: {
    //       name: "Super Admin",
    //       email: "ceo.galloptech@gmail.com",
    //       phoneNumber: 0,
    //     },
    //     companyProfile: [],
    //   },
    // },
    profile: tokenStorage?.user || null,
    remember: false,
  });

  const currentPage = routes?.find((route: RouteObject) => {
    if (
      route.path !== "*" &&
      route.path &&
      matchPath(route.path, location.pathname)
    ) {
      return true;
    }
    if (route.path === "*") {
      return !routes.some(
        (r) => r.path !== "*" && matchPath(r.path || "", location.pathname),
      );
    }
  }) as AppRouteType;
  const onSignIn = async (credentials: {
    email: string;
    password: string;
    remember: boolean;
  }) => {
    try {
      setUserData({ loading: true });
      const response = await accountService.signIn(credentials);
      console.log("response", response);
      const authenticationResult = response?.data as unknown as SuccessResponse;
      if (authenticationResult?.success) {
        const rs =
          authenticationResult?.data as unknown as AuthenticationResult;
        setToken({
          ...rs,
          remember: credentials.remember,
        });
        setUserData({
          accessToken: rs.accessToken,
          profile: rs.user,
          loading: false,
          isFirstLoading: false,
        });
      }
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };
  const onRefreshToken = async () => {
    try {
      const refreshToken = getRefreshToken();
      const response = await accountService.refreshToken(refreshToken);
      const AuthenticationResult = response?.data?.AuthenticationResult;
      if (AuthenticationResult) {
        setToken(AuthenticationResult);
        notificationSocketService.connect(AuthenticationResult.accessToken);
        setUserData({
          accessToken: AuthenticationResult.accessToken,
        });
      }
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };

  const onSignOut = async () => {
    notificationSocketService.disconnect();

    try {
      if (userData?.profile) {
        const accessToken = getAccessToken();
        await accountService.signOut({ accessToken });
      }
    } catch (error) {
      console.error(error);
    }
    setUserData({
      isFirstLoading: false,
      loading: false,
      accessToken: null,
      isAuth: false,
      profile: null,
    });
    removeToken();
    navigate("/login", { replace: true });
  };
  const getProfile = async () => {
    try {
      setUserData({ loading: true });
      const user = await accountService.getProfile();
      setUserData({
        isFirstLoading: false,
        loading: false,
        profile: user?.data.data,
        isAuth: true,
      });
    } catch (error) {
      console.error(error);
    }
  };
  const onForgot = async (credentials: {
    email: string;
    frontendUrl?: string;
  }): Promise<ResponseForgot> => {
    try {
      setUserData({ loading: true });
      const response = await accountService.forgetPassword(credentials);
      setUserData({ loading: false });
      return response?.data;
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };
  const onVerify = async (credentials: {
    token: string;
  }): Promise<ResponseReset> => {
    try {
      setUserData({ loading: true });
      const response = await accountService.verifyToken(credentials);
      // console.log('response', response);
      setUserData({ loading: false });
      return {
        email: response?.data?.data?.user?.email,
        success: response?.data?.success,
        expiredAt: response?.data?.data?.expiredAt, // Thêm dòng này
      };
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };
  const onResetPass = async (credentials: {
    email: string;
    password: string;
    token: string;
    frontendUrl?: string;
  }): Promise<ResponseReset> => {
    try {
      setUserData({ loading: true });
      const response = await accountService.resetPassword(credentials);
      setUserData({ loading: false });
      return response?.data;
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw error;
    }
  };
  useEffect(() => {
    const tokenStorage = getAuthenticationResult();
    if (!userData?.accessToken || !tokenStorage?.ExpiryDate) return;

    const expiryMoment = moment(tokenStorage.ExpiryDate);
    const now = moment();
    const timeUntilExpiry = expiryMoment.diff(now, "milliseconds");
    const refreshBefore = Math.max(timeUntilExpiry - 1000 * 60 * 60, 0);
    const timer = setTimeout(() => {
      onRefreshToken();
    }, refreshBefore);

    return () => clearTimeout(timer);
  }, [userData?.accessToken]);

  useEffect(() => {
    if (!userData?.accessToken) {
      setUserData({ isFirstLoading: false });
    }
    if (userData?.accessToken && !userData?.profile) {
      getProfile();
    }
  }, [userData?.accessToken]);

  useEffect(() => {
    if (userData?.accessToken && userData?.profile) {
      // check permission
      // if (currentPage?.roles) {
      //   navigate('/404', { replace: true });
      // }
      if (location.pathname === "/") {
        navigate("/command-list", { replace: true });
        return;
      }
      console.log("currentPage: ", currentPage);
      if (currentPage?.isPublic && !currentPage.path?.includes("*")) {
        navigate("/command-list", { replace: true });
      }
    } else if (!userData?.accessToken && !currentPage?.isPublic) {
      navigate("/login", { replace: true });
    }
  }, [userData?.accessToken, userData?.profile, currentPage?.isPublic]);

  return (
    <AuthContext.Provider
      value={{
        onSignIn,
        onSignOut,
        getProfile,
        onRefreshToken,
        onForgot,
        onVerify,
        onResetPass,
        loading: userData.loading,
        accessToken: userData.accessToken,
        profile: userData.profile,
        isAuth: userData.isAuth,
      }}
    >
      {userData?.isFirstLoading ? <LoadingFallback /> : children}
    </AuthContext.Provider>
  );
}

const useAuthContext = () => useContext(AuthContext);
export default useAuthContext;
